import { removeUndefinedFields, replaceUndefinedWithDelete } from 'lib/helpers';
import { OrderEditableData, OrderModel } from 'lib/model/objects/orderModel';
import { NewspaperOrder } from 'lib/types/newspaperOrder';
import {
  Order,
  isAdvertiserWithOrganizationOrder,
  isAnonymousUserOrder,
  isPublisherAsAdvertiserWithOrganizationOrder,
  isIndividualAdvertiserOrder,
  isAnonymousOrder,
  AnonymousOrderContactInfo
} from 'lib/types/order';
import { merge } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { centsToExtendedCurrency } from 'lib/utils/rates';
import { logAndCaptureCriticalError } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { OrganizationType, Product } from 'lib/enums';
import { isObituary } from 'lib/types/ad';
import { Obituary } from 'lib/types/obituary';
import { Classified } from 'lib/types/classified';
import { AdModel } from 'lib/model/objects/adModel';
import { logInfo, logWarn } from 'utils/logger';
import { BLOCK_SUBMIT_ID } from 'components/LoadingState';
import { COLUMN_SUPPORT_EMAIL } from 'lib/constants';
import { useAppSelector } from 'redux/hooks';
import {
  selectActiveOrganization,
  selectIsPublisher,
  selectUser
} from 'redux/auth';
import { createOrUpdateAnonymousFiler } from 'utils/users';
import { getOrCreateCustomer } from 'lib/notice/customer';
import { getOrCreateUserStripeId } from 'services/billing';
import { getModelFromRef } from 'lib/model';
import { UserModel } from 'lib/model/objects/userModel';
import { isDefined } from 'lib/helpers/typeGuards';
import { getFirebaseContext } from 'utils/firebase';
import { CustomerTypeString } from '../filters/helpers';
import FormWrapper from './FormWrapper';
import Placemat from './Placemat';
import { useAdForm } from './contexts/AdFormStatusProvider';
import { completeOrder } from './helpers/completeOrder';
import { computeOrderIOC } from './helpers/computeOrderIOC';
import { usePricing } from './hooks/usePricing';
import { PlacementFlowStep } from './placementFlowStep';
import DraftContent from './steps/DraftContent';
import FlowChoiceStep from './steps/FlowChoice';
import { FuneralHomeCustomerInformation } from './steps/FlowChoice/funeralHomeCustomerInformation';
import OrderSummary from './steps/OrderSummary';
import PersonalDetail, { MINIMUM_ORDER } from './steps/PersonalDetail';
import ProvideVerification from './steps/ProvideVerification';
import SelectPublication from './steps/SelectPublication';
import SelectSchedules from './steps/SelectSchedules';
import getOrCreateCustomerWithCustomerOrganization from './helpers/getOrCreateCustomerWithCustomerOrganization';
import CategoryChoiceStep from './steps/CategoryChoice';
import { useOrderInvoiceFlow } from './hooks/useOrderInvoiceFlow';
import getOrCreateCustomerWithoutOrganization from './helpers/getOrCreateCustomer';
import { useOrderRequiresUpfrontPayment } from './hooks/useOrderRequiresUpfrontPayment';

export const STALE_EDIT_SESSION_MESSAGE =
  'This order was updated in another session. Please return to the previous page to start a new edit session.';

export type NewspaperOrdersFormData = Partial<NewspaperOrder>[];

export type OrderFormData = Partial<Order>;

export enum SuccessModalType {
  EditConfirmation = 'Your edits have been processed.',
  FreeOrderSubmission = 'Your order has been submitted successfully.',
  MarkInvoicePaidConfirmation = 'The invoice has been marked as paid.',
  IOCOrderSubmission = 'Your order has been submitted successfully.'
}

type Step = {
  component: ReactNode;
  dependencyCheck?: () => string;
  onClickNext?: () => Promise<void>;
};

type PlacementFlowStepSelectorProps = {
  orderModel: OrderModel;
  adModel: AdModel;
  steps: PlacementFlowStep[];
  draftNewspaperOrders?: NewspaperOrdersFormData;
  version: number;
  product: Product;
  editData: OrderEditableData | undefined;
};

function PlacementFlowStepSelector({
  orderModel,
  adModel,
  draftNewspaperOrders,
  steps,
  product,
  version,
  editData
}: PlacementFlowStepSelectorProps) {
  const isInitialPlacementFlow =
    orderModel.modelData.activeVersion === adModel.modelData.orderVersion;

  const isPublisher = useAppSelector(selectIsPublisher);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const user = useAppSelector(selectUser);

  const {
    currentStep,
    updateCurrentStep,
    userError,
    setUserError,
    clearUserError,
    isFormLoading
  } = useAdForm();
  const [newspaperOrdersFormData, setNewspaperOrdersFormData] =
    useState<NewspaperOrdersFormData>(draftNewspaperOrders || []);
  const [orderFormData, setOrderFormData] = useState<OrderFormData>(
    orderModel.modelData
  );
  const [adFormData, setAdFormData] = useState<Partial<Obituary | Classified>>(
    adModel.modelData
  );
  const isPastContentStep =
    steps.includes(PlacementFlowStep.Summary) &&
    currentStep > steps.indexOf(PlacementFlowStep.Content) + 1;
  const [showSuccessModal, setShowSuccessModal] =
    useState<SuccessModalType | null>(null);

  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [invoiceMarkedAsPaid, setInvoiceMarkedAsPaid] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [orderIsIOC, setOrderIsIOC] = useState(false);

  const isEditing = orderModel.modelData.activeVersion !== version;

  async function assertNotStaleEditSession() {
    if (!editData) {
      return;
    }
    await orderModel.refreshData();

    if (orderModel.modelData.activeVersion > version) {
      setUserError(STALE_EDIT_SESSION_MESSAGE);
      logWarn(STALE_EDIT_SESSION_MESSAGE, {
        orderId: orderModel.id,
        userId: user?.id || 'unknown'
      });
      throw new Error(STALE_EDIT_SESSION_MESSAGE);
    }
  }

  async function updateModels() {
    await assertNotStaleEditSession();

    const sanitizedOrderFormData = { ...orderFormData };
    delete sanitizedOrderFormData.activeVersion;

    await orderModel.ref.update(
      replaceUndefinedWithDelete(
        getFirebaseContext(),
        isAnonymousOrder(sanitizedOrderFormData)
          ? merge({ ...MINIMUM_ORDER }, sanitizedOrderFormData)
          : sanitizedOrderFormData
      )
    );
    // TODO(goodpaul): handle delete sentinel values in the model code
    await orderModel.refreshData();

    await adModel.ref.update(removeUndefinedFields(adFormData));

    /**
     * We generate multiple properties asynchronously in the back end (see `priceAdWithSettings`
     * in `updateNewspaperOrderPricing`) and need to delete them here to ensure we do not overwrite
     * them with our FE form data
     */
    const sanitizedNewspaperOrdersFormData = newspaperOrdersFormData.map(
      nofd => {
        const sanitized = { ...nofd };
        delete sanitized.pdfStoragePath;
        delete sanitized.pricing;
        return sanitized;
      }
    );

    await orderModel.updateNewspaperOrders(
      removeUndefinedFields(sanitizedNewspaperOrdersFormData),
      version,
      { allowInsert: true }
    );
  }

  const {
    consolidatedOrderPricing,
    handleRefreshOrderPrice,
    priceLoading,
    priceError,
    priceIsStale,
    oldTotalInCents,
    isPartialRefund,
    refundRequestedInCents
  } = usePricing({
    newspaperOrdersFormData,
    orderModel,
    adFormData,
    version,
    isEditing,
    persistModelsForPricing: updateModels
  });
  const {
    requireNewInvoice,
    isLoading: isInvoiceFlowLoading,
    error: invoiceFlowError,
    newInvoiceId,
    setNewInvoiceId
  } = useOrderInvoiceFlow({
    orderModel,
    isEditing,
    version,
    step: steps[currentStep - 1],
    isPastContentStep
  });

  const requiresUpfrontPayment = useOrderRequiresUpfrontPayment({
    orderModel,
    version
  });
  const submitWithoutPaying = requiresUpfrontPayment === false;

  const loading =
    isSubmitLoading || isInvoiceFlowLoading || priceLoading || isFormLoading;
  const userErrorMessage = priceError || invoiceFlowError || userError;

  const chosenFlow = isAdvertiserWithOrganizationOrder(orderFormData)
    ? CustomerTypeString.FUNERAL_HOME
    : CustomerTypeString.INDIVIDUAL;

  /**
   * TODO: Why are we only checking for this for customers with organizations?
   * Should we also be using chosen customers for individual advertisers?
   */
  const advertiserWithOrgCustomer =
    isPublisherAsAdvertiserWithOrganizationOrder(orderFormData)
      ? orderFormData.advertiserCustomer
      : undefined;

  const goToNextStep = () => {
    clearUserError();
    updateCurrentStep(currentStep + 1);
  };

  const onSubmit = async () => {
    clearUserError();

    const { dependencyCheck } = stepMap[steps[currentStep - 1]];

    const dependencyError = dependencyCheck && dependencyCheck();

    if (dependencyError) {
      setUserError(dependencyError);
      return;
    }

    // TODO: Survey the group to see if making the hidden input field required would be acceptable
    // Check for the hidden input field to block form submission
    const blockSubmitInput = document.getElementById(BLOCK_SUBMIT_ID);
    if (blockSubmitInput) {
      logInfo('Navigation blocked due to form loading');
      return;
    }

    setSubmitLoading(true);

    try {
      // currentStep is 1-indexed so this if statement will not be executed for the last step
      if (currentStep < steps.length) {
        // We don't want to overrite the models at the last step because the BE has changed them after creating the invoice
        await updateModels();
      }

      const { onClickNext } = stepMap[steps[currentStep - 1]];

      if (onClickNext) {
        await onClickNext();
      }
    } catch (e: any) {
      if (e.message === STALE_EDIT_SESSION_MESSAGE) {
        return;
      }
      logAndCaptureCriticalError(
        ColumnService.ORDER_PLACEMENT,
        e,
        `Failure during ${product} placement flow ${
          steps[currentStep - 1]
        } step`,
        {
          ...(isAnonymousUserOrder(orderModel.modelData)
            ? { contactEmail: orderModel.modelData.contactEmail }
            : { userId: orderModel.modelData.user.id }),
          adId: adModel.id,
          product,
          service: ColumnService.ORDER_PLACEMENT
        }
      );

      setUserError(
        `Could not load order. Please try again or contact ${COLUMN_SUPPORT_EMAIL} for assistance.`
      );

      return;
    } finally {
      setSubmitLoading(false);
    }

    if (currentStep < steps.length) {
      goToNextStep();
    }
  };

  useEffect(() => {
    if (newInvoiceId) {
      void handleRefreshOrderPrice();
    }
  }, [newInvoiceId]);

  const getButtonText = () => {
    const shouldSubmitInsteadOfPay =
      !requireNewInvoice ||
      consolidatedOrderPricing?.totalInCents === 0 ||
      submitWithoutPaying ||
      orderIsIOC;
    if (
      shouldSubmitInsteadOfPay &&
      steps[currentStep - 1] === PlacementFlowStep.Summary
    ) {
      return 'Submit';
    }
    if (
      isPartialRefund &&
      steps[currentStep - 1] === PlacementFlowStep.Summary
    ) {
      return `Refund $${centsToExtendedCurrency(refundRequestedInCents)}`;
    }
    if (currentStep < steps.length || !consolidatedOrderPricing) {
      return 'Next';
    }

    if (invoiceMarkedAsPaid) {
      return `Received $${centsToExtendedCurrency(
        consolidatedOrderPricing.totalInCents
      )}`;
    }

    return `Pay $${centsToExtendedCurrency(
      consolidatedOrderPricing.totalInCents
    )}`;
  };

  const stepMap: Record<PlacementFlowStep, Step> = {
    [PlacementFlowStep.CustomerType]: {
      component: (
        <Placemat>
          <FlowChoiceStep
            chosenCustomer={advertiserWithOrgCustomer}
            onChosenCustomerChange={(
              advertiser: FuneralHomeCustomerInformation | undefined
            ) => {
              setOrderFormData({
                ...orderFormData,
                firstName: undefined,
                lastName: undefined,
                contactEmail: undefined,
                advertiserCustomer: advertiser?.ref,
                advertiser: advertiser?.user.ref,
                advertiserOrganization: advertiser?.organization.ref
                  ? advertiser.organization.ref
                  : null
              });

              if (isObituary(adFormData)) {
                // if advertiser is an individual, clear death verification
                if (!advertiser?.organization) {
                  setAdFormData({
                    ...adFormData,
                    deathVerification: {}
                  });
                }
                // if advertiser is a funeral home, set death verification to verified
                else if (
                  advertiser?.organization.data().organizationType ===
                  OrganizationType.funeral_home.value
                ) {
                  setAdFormData({
                    ...adFormData,
                    deathVerification: {
                      verifiedAt: getFirebaseContext().timestamp()
                    }
                  });
                }
                // TODO: handle other organization types for customer selection on obits
                else {
                  logAndCaptureCriticalError(
                    ColumnService.ORDER_PLACEMENT,
                    new Error(
                      'Unimplemented death verification behavior for selecting an advertiser that is not a funeral home'
                    ),
                    'Unimplemented death verification behavior for selecting an advertiser that is not a funeral home',
                    {
                      adId: adModel.id,
                      product,
                      service: ColumnService.ORDER_PLACEMENT
                    }
                  );
                  setAdFormData({
                    ...adFormData,
                    deathVerification: {}
                  });
                }
              }
            }}
          />
        </Placemat>
      )
    },
    [PlacementFlowStep.Category]: {
      component: (
        <CategoryChoiceStep
          product={product}
          inputData={adFormData}
          newspaperOrdersFormData={newspaperOrdersFormData}
          onSetNewspaperOrdersFormData={setNewspaperOrdersFormData}
          orderModel={orderModel}
          onChangeAd={adData => {
            setAdFormData({ ...adFormData, ...adData });
          }}
        />
      )
    },
    [PlacementFlowStep.Details]: {
      component: (
        <Placemat>
          <PersonalDetail
            product={product}
            inputData={orderFormData}
            setInputData={setOrderFormData}
          />
        </Placemat>
      ),
      onClickNext: async () => {
        if (
          isPublisher &&
          activeOrganization &&
          product === Product.Classified
        ) {
          const contactInfo = orderFormData as AnonymousOrderContactInfo;
          const userRef = await createOrUpdateAnonymousFiler(
            activeOrganization,
            {
              email: contactInfo.contactEmail,
              firstName: contactInfo.firstName,
              lastName: contactInfo.lastName,
              phone: contactInfo.phone,
              address: contactInfo.addressLine1,
              addressLine2: contactInfo.addressLine2,
              city: contactInfo.city,
              state: contactInfo.state,
              zipCode: contactInfo.zip,
              organizationName: contactInfo.organizationName
            },
            { invitedBy: activeOrganization.ref }
          );

          const userModel = await getModelFromRef(
            UserModel,
            getFirebaseContext(),
            userRef
          );

          await getOrCreateUserStripeId(userModel, undefined);

          const customer = await getOrCreateCustomer(
            getFirebaseContext(),
            userModel,
            activeOrganization
          );

          const allowedOrganizationsResult =
            await userModel.getAllowedOrganizations();

          if (allowedOrganizationsResult.error) {
            throw allowedOrganizationsResult.error;
          }

          const advertiserOrganization =
            allowedOrganizationsResult.response.find(
              allowedOrganizationSnap =>
                allowedOrganizationSnap.modelData.name ===
                contactInfo.organizationName
            )?.ref;

          setOrderFormData({
            ...orderFormData,
            firstName: undefined,
            lastName: undefined,
            phone: undefined,
            contactEmail: undefined,
            advertiser: userRef,
            advertiserCustomer: customer.ref,
            ...(advertiserOrganization && { advertiserOrganization })
          });
        }
      }
    },
    [PlacementFlowStep.Publication]: {
      component: (
        <Placemat>
          <SelectPublication
            newspaperOrdersFormData={newspaperOrdersFormData}
            onNewspaperOrdersFormDataChange={setNewspaperOrdersFormData}
            product={product}
            orderModel={orderModel}
            inputData={adFormData}
            onUpdateAd={setAdFormData}
          />
        </Placemat>
      ),
      dependencyCheck: () => {
        if (newspaperOrdersFormData.length === 0) {
          return 'Please select at least one publication.';
        }

        return '';
      },
      onClickNext: async () => {
        if (
          isAdvertiserWithOrganizationOrder(orderFormData) ||
          isIndividualAdvertiserOrder(orderFormData)
        ) {
          const promises = newspaperOrdersFormData
            .map(newspaperOrder => newspaperOrder.newspaper?.id)
            .filter(isDefined)
            .map(async newspaperId => {
              const userId = orderFormData.advertiser.id;
              // If advertiserOrganization is not found then create customer only
              await getOrCreateCustomerWithoutOrganization({
                userId,
                publisherId: newspaperId
              });
              if (orderFormData.advertiserOrganization) {
                await getOrCreateCustomerWithCustomerOrganization({
                  userId,
                  organizationId: orderFormData.advertiserOrganization.id || '',
                  publisherId: newspaperId
                });
              }
            });

          await Promise.all(promises);
        }
      }
    },
    [PlacementFlowStep.Schedule]: {
      component: (
        <SelectSchedules
          product={product}
          newspaperOrdersFormData={newspaperOrdersFormData}
          onNewspaperOrdersFormDataChange={setNewspaperOrdersFormData}
          isInitialPlacementFlow={isInitialPlacementFlow}
          editData={editData}
          orderModel={orderModel}
        />
      )
    },
    [PlacementFlowStep.Verification]: {
      component: (
        <Placemat>
          <ProvideVerification
            chosenFlow={chosenFlow}
            inputData={adFormData}
            setInputData={value => {
              setAdFormData(value);
            }}
          />
        </Placemat>
      )
    },
    [PlacementFlowStep.Content]: {
      component: (
        <DraftContent
          adData={adFormData}
          onAdChange={setAdFormData}
          newspaperOrdersFormData={newspaperOrdersFormData}
          onUpdateNewspaperOrdersFormData={setNewspaperOrdersFormData}
          consolidatedOrderPricing={consolidatedOrderPricing}
          priceLoading={priceLoading}
          priceIsStale={priceIsStale}
          order={orderModel}
          version={version}
          canEditContent={
            isInitialPlacementFlow ||
            !!(editData && Object.values(editData).every(ed => ed.canEdit))
          }
        />
      ),
      onClickNext: async () => {
        const [iocErr, isIOC] = await computeOrderIOC(
          orderModel,
          newspaperOrdersFormData
        );
        if (!iocErr) {
          setOrderIsIOC(isIOC);
        } else {
          setOrderIsIOC(false);
        }
      }
      /**
       * If we insert a step between the Content & Summary steps, we should recalculate the price in the onClickNext handler.
       * Invoice creation is deferred to a useEffect that fires when requireNewInvoice is determined on the Summary step.
       */
    },
    [PlacementFlowStep.Summary]: {
      component: (
        <Placemat>
          <OrderSummary
            newspaperOrdersFormData={newspaperOrdersFormData}
            invoiceLoading={isInvoiceFlowLoading}
            submitLoading={isSubmitLoading}
            orderModel={orderModel}
            adData={adFormData}
            product={product}
            version={version}
            consolidatedOrderPricing={consolidatedOrderPricing}
            requireNewInvoice={requireNewInvoice}
            showSuccessModal={showSuccessModal}
            onCouponUpdate={handleRefreshOrderPrice}
            onNewInvoiceCreated={setNewInvoiceId}
            invoiceMarkedAsPaid={invoiceMarkedAsPaid}
            setInvoiceMarkedAsPaid={setInvoiceMarkedAsPaid}
            onPaymentNotesUpdate={setPaymentNotes}
            isPartialRefund={isPartialRefund}
            oldTotalInCents={oldTotalInCents}
            orderIsIOC={orderIsIOC}
          />
        </Placemat>
      ),
      onClickNext: async () => {
        if (isInvoiceFlowLoading) return;

        if (!newInvoiceId && requireNewInvoice) {
          logAndCaptureCriticalError(
            ColumnService.ORDER_PLACEMENT,
            new Error('newInvoiceId not set'),
            'Cannot proceed to complete order because newInvoiceId is not set and new invoice is required',
            {
              adId: adModel.id,
              product,
              service: ColumnService.ORDER_PLACEMENT
            }
          );
          setUserError(
            'Invoice not found. Please click go to the previous step and try again.'
          );
          return;
        }
        const [completeError, result] = await completeOrder({
          orderModel,
          version,
          consolidatedOrderPricing,
          newInvoiceId,
          refundRequestedInCents,
          invoiceMarkedAsPaid,
          paymentNotes,
          submitWithoutPaying,
          user,
          orderIsIOC
        });
        if (completeError) {
          setUserError('Failed to submit order. Please try again.');
          return;
        }
        if (result.showModal) {
          setShowSuccessModal(result.showModal);
        }
        if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
        }
      }
    }
  };

  return (
    <FormWrapper
      onSubmit={onSubmit}
      submitText={getButtonText()}
      steps={steps}
      loading={loading}
      newspaperOrdersFormData={newspaperOrdersFormData}
      userError={userErrorMessage}
      product={product}
    >
      {stepMap[steps[currentStep - 1]].component}
    </FormWrapper>
  );
}

export default PlacementFlowStepSelector;
